import React, { useEffect, useState } from "react";
import {
  Modal,
  Avatar,
  Text,
  Button,
  Grid,
  Box,
  createStyles,
  Loader,
  Divider,
  Center,
  ActionIcon,
  rem,
  RingProgress,
} from "@mantine/core";
import { IconCheck } from "@tabler/icons-react";
import fetchResourceIcon from "../assets/img/fetchingResourceLogo.svg";
import dbStoringIcon from "../assets/img/storingDBLogo.svg";
import { athenaHandler, capitalizeString, cernerHandler, ecwHandler, extractValuesBySystem, getAccessToken } from "../utils";
import _ from "lodash";
import { MedplumClient } from "@medplum/core";
import { API_ENDPOINTS } from "../api";
import axios from "axios";
import { ExtensionObject, getTokensAndUrl } from "../pages/HomePage.utils";
import { useSelector } from "react-redux";

export interface PatientResource {
  id: string;
  birthDate: string;
  telecom: [];
  gender: string;
  name: {
    id: string;
    use: string;
    text: string;
    family: string;
    given: string[];
    prefix: string[];
  }[];
  address: {
    id: string;
    use: string;
    text: string;
    line: string[];
    city: string;
    state: string;
    postalCode: string;
  }[];
  image: string;
}

interface Props {
  isOpen: boolean;
  onClose: (patientId: string) => void;
  ehrType: string;
  ehrImage: string;
  ehrName: string;
  patientDetails: PatientResource;
  medplum: MedplumClient;
  searchTerm: string;
  setIsPatientModalOpen: (val: boolean) => void;
}

const useStyles = createStyles(() => ({
  flex: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  topHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    background: "#F1F9FF",
    padding: ".5rem 1rem .5rem .5rem",
    borderRadius: "8px",
    marginBottom: "24px",
  },
  detailGrid: {
    marginTop: "24px",
    marginBottom: "32px",
    border: "1px solid",
  },
  avatar: {
    border: "none",
    background: "none",
  },
  name: {
    color: "#262930",
    fontSize: "18px",
    fontWeight: 600,
    lineHeight: "30px",
    letterSpacing: "0px",
  },
  ehrType: {
    fontSize: "14px",
    fontWeight: 600,
    lineHeight: "21px",
    letterSpacing: "0px",
    color: "#818999",
  },
  ehrTypeName: {
    fontSize: "16px",
    fontWeight: 600,
    lineHeight: "24px",
    letterSpacing: "0px",
    color: "#262930",
    textTransform: 'capitalize'
  },
  label: {
    fontSize: "16px",
    fontWeight: 500,
    lineHeight: "22px",
    letterSpacing: "0px",
    color: "#818999",
    marginBottom: "8px",
  },
  button: {
    textAlign: "right",
    marginTop: "32px",
    marginBottom: "10px",
  },
  value: {
    fontSize: "16px",
    fontWeight: 500,
    lineHeight: "22px",
    letterSpacing: "0px",
    color: "#262930",
  },
  logo: {
    width: "48px",
    marginRight: "5px",
    height: '40px',
    marginTop: '3px'
  },
  stepContainer: {
    height: "122px",
    padding: "16px",
    borderRadius: "8px",
    border: "1px solid #C9CDD3",
    display: "flex",
    justifyContent: "space-between",
    marginTop: "32px",
  },
  step: {
    width: "150px",
    height: "90px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
  },
  disableText: {
    fontSize: "14px",
    fontWeight: 500,
    lineHeight: "20px",
    letterSpacing: "0px",
    textAlign: "center",
    color: "#99A0AD",
    marginTop: "10px",
  },
  enableText: {
    fontSize: "14px",
    fontWeight: 500,
    lineHeight: "20px",
    letterSpacing: "0px",
    textAlign: "center",
    color: "#465668",
    marginTop: "10px",
  },
  divider: {
    width: "20%",
  },
  icon: {
    position: "absolute",
    top: 10,
    left: 10,
  },
}));

const detailLabels = ["Date of Birth", "Mobile", "Gender", "Email", "Address"];
const PatientInfoModal: React.FC<Props> = ({
  isOpen,
  onClose,
  ehrType,
  ehrImage,
  ehrName,
  patientDetails,
  medplum,
  searchTerm,
  setIsPatientModalOpen,
}) => {
  const [showStep, setSetshowStep] = useState(false);
  const [detailValues, setDetailValues] = useState<string[]>();
  const [loadingStep, setLoadingStep] = useState(0);
  const [storedPatientId, setStoredPatientId] = useState("");
  const { classes } = useStyles();
  const profile = useSelector((state: any) => state.app.profile);

  const steps = [
    { label: "Observations", icon: fetchResourceIcon },
    { label: "Conditions", icon: fetchResourceIcon },
    { label: "Medications", icon: fetchResourceIcon },
    { label: "Immunizations", icon: fetchResourceIcon },
    { label: "Diagnostics", icon: fetchResourceIcon },
    { label: "Storing in HealthConnect", icon: dbStoringIcon },
  ];

  useEffect(() => {
    let timer: any;
    if (showStep) {
      const timer = setTimeout(() => {
        setLoadingStep((prevStep) =>
          prevStep === steps.length - 1 ? 0 : prevStep + 1
        );
      }, 5000);
    }

    if (loadingStep === steps.length - 1) {
      _.debounce(() => onClose(storedPatientId), 4000)();
    }

    return () => clearTimeout(timer);
  }, [loadingStep, steps.length, showStep]);

  useEffect(() => {
    let phone = "";
    let email = "";
    if (patientDetails?.telecom) {
      phone = extractValuesBySystem(patientDetails?.telecom, "phone");
      email = extractValuesBySystem(patientDetails?.telecom, "email");
    }
    setDetailValues([
      patientDetails?.birthDate,
      phone,
      capitalizeString(patientDetails?.gender),
      email,
      patientDetails?.address?.[0]?.text,
    ]);
  }, [patientDetails]);

  const handleButtonClick = async () => {
    setSetshowStep(true);
    const activeLogin = getAccessToken();
    const tokensAndUrl = getTokensAndUrl(profile?.extension as ExtensionObject[] || [], searchTerm);


    let createdPatient;
    let createdPatientId;


    if (ehrType.toLowerCase() == "eclinicalworks") {
      const newIdentifier = [
        {
          system: "ecw_patient_id",
          value: patientDetails?.id,
        },
      ]
      const patient = patientDetails as any
      patient.identifier = [...patient.identifier, ...newIdentifier];
      createdPatient = await createPatient(activeLogin, patient) as any;
      createdPatientId = createdPatient?.data?.patientResource?.id??createdPatient?.data?.updatedResource?.id;
      setStoredPatientId(createdPatientId);
    }
    else if (ehrType.toLowerCase() !== "athenahealth") {
      createdPatient = await createPatient(activeLogin, patientDetails) as any;
      createdPatientId = createdPatient?.data?.patientResource?.id??createdPatient?.data?.updatedResource?.id;
      setStoredPatientId(createdPatientId);
    }


    if (ehrType.toLowerCase() == "cerner") {
      const patientPayload = {
        eventType: "patientResources",
        cernerPatientId: patientDetails?.id,
        storedPatientId: createdPatientId,
        clientId: tokensAndUrl?.[searchTerm]?.clientId,
        accessToken: tokensAndUrl?.[searchTerm]?.accessToken,
      };
      await cernerHandler(medplum, patientPayload);
    } else if (ehrType.toLowerCase() == "eclinicalworks") {
      const patientPayload = {
        eventType: "patientResources",
        patientId: patientDetails?.id,
        storedPatientId: createdPatientId,
        clientId: tokensAndUrl?.[searchTerm]?.clientId,
        accessToken: tokensAndUrl?.[searchTerm]?.accessToken,
      };
      await ecwHandler(medplum, patientPayload);
    }
    else if (ehrType.toLowerCase() == "athenahealth") {
      const athenaPateint = await handleAthenaPatientSearch(tokensAndUrl);
      if (athenaPateint?.body?.storedId) delete athenaPateint.body.storedId
      const newIdentifier = [
        {
          system: "athena_patient_id",
          value: athenaPateint?.body?.id,
        },
      ]
      const patient = athenaPateint?.body as any
      patient.identifier = [...patient.identifier, ...newIdentifier];
      const createdPatient = await createPatient(activeLogin, patient) as any;
      setStoredPatientId(createdPatient?.data?.patientResource?.id??createdPatient?.data?.updatedResource?.id);


      const createdPatientId = createdPatient?.data?.patientResource?.id;
      const patientPayload = {
        eventType: "patientResources",
        athenaPatientId: patientDetails?.id,
        storedPatientId: createdPatientId,
        clientId: tokensAndUrl?.[searchTerm]?.clientId,
        accessToken: tokensAndUrl?.[searchTerm]?.accessToken,
      };
      await athenaHandler(medplum, patientPayload);
    } else if (ehrType.toLowerCase() == "epic") {
      const body = {
        clientId: tokensAndUrl?.[searchTerm]?.clientId,
        accessToken: tokensAndUrl?.[searchTerm]?.accessToken,
        patientId: createdPatientId,
        epicPatientId: patientDetails?.id,
      };

      const contentType = "application/json";
      const options = {
        headers: {
          Authorization: `Bearer ${activeLogin.accessToken}`,
        },
      };

      try {
        await medplum.executeBot(
          {
            system: 'https://api-stage.healthconnect.systems/bots',
            value: 'epic-service'
          },
          body,
          contentType,
          options
        );
      } catch (error) {
        console.error(error);
      }
    }
  };

  const createPatient = async (activeLogin: any, patientData: any) => {
    const storedPatient = await medplum.searchOne('Patient', `identifier=${patientData.id}`)
    if (storedPatient) {
      const updatePatient = {...patientData, id: storedPatient.id}
      return await axios.put(
        API_ENDPOINTS.UPDATE_RESOURCE +'/' + storedPatient.id,
        updatePatient,
        {
          headers: {
            Authorization: `Bearer ${activeLogin.accessToken}`,
          },
        }
      );
    } else
      return await axios.post(
        API_ENDPOINTS.UPLOAD_PATIENT,
        patientData,
        {
          headers: {
            Authorization: `Bearer ${activeLogin.accessToken}`,
          },
        }
      );
  }

  const handleAthenaPatientSearch = async (tokensAndUrl: any) => {
    const patientPayload = {
      eventType: "patient",
      clientId: tokensAndUrl?.[searchTerm]?.clientId,
      accessToken: tokensAndUrl?.[searchTerm]?.accessToken,
      patientId: patientDetails?.id
    };
    const athenapatientRes = await athenaHandler(medplum, patientPayload);
    return athenapatientRes
  }

  interface Props {
    disabled?: boolean;
  }

  const StepComponent: React.FC<Props> = ({ disabled }) => {
    const Progress = () => {
      return (
        <RingProgress
          sections={[{ value: 100, color: "teal" }]}
          size={55}
          thickness={5}
          label={
            <Center>
              <ActionIcon color="teal" variant="light" radius="xl" size="md">
                <IconCheck style={{ width: rem(22), height: rem(22) }} />
              </ActionIcon>
            </Center>
          }
        />
      );
    };

    return (
      <Box className={classes.stepContainer}>
        {steps.map((step, index) => (
          <React.Fragment key={index}>
            <Box className={classes.step}>
              {index < loadingStep ? (
                <Progress />
              ) : index === loadingStep ? (
                <Box style={{ position: "relative" }}>
                  <Loader
                    size={"lg"}
                    color="green"
                    style={{ marginRight: "8px" }}
                  />
                  <ActionIcon
                    color="gray"
                    variant="light"
                    radius="xl"
                    size="sm"
                    className={classes.icon}
                  >
                    <img
                      src={
                        index === steps.length - 1
                          ? dbStoringIcon
                          : fetchResourceIcon
                      }
                      style={{ width: rem(22), height: rem(22) }}
                    />
                  </ActionIcon>
                </Box>
              ) : (
                <img
                  src={
                    index === steps.length - 1
                      ? dbStoringIcon
                      : fetchResourceIcon
                  }
                />
              )}
              <Text className={classes.disableText}>
                {index !== steps.length - 1 && index === loadingStep
                  ? `Fetching ${step.label}`
                  : index < loadingStep
                    ? `Fetched ${step.label}`
                    : step.label}
              </Text>
            </Box>
            {index !== steps.length - 1 && (
              <Divider my="xs" className={classes.divider} />
            )}
          </React.Fragment>
        ))}
      </Box>
    );
  };

  return (
    <Modal
      opened={isOpen}
      onClose={() => setIsPatientModalOpen(false)}
      withCloseButton={false}
      centered
      size={"xl"}
    >
      <Modal.CloseButton />
      <Box className={classes.topHeader}>
        <Box className={classes.flex}>
          <Avatar
            size={50}
            src={patientDetails?.image}
            className={classes.avatar}
          />
          <Box style={{ marginLeft: "10px" }}>
            <Text className={classes.name}>
              {ehrType.toLocaleLowerCase() === "athenahealth" ? patientDetails?.name?.[0]?.given?.join(" ") : patientDetails?.name?.[0]?.text}
            </Text>
          </Box>
        </Box>
        <Box>
          <Text className={classes.ehrType}>EHR Type</Text>
          <Box className={classes.flex}>
            <img src={ehrImage} alt={ehrName} className={classes.logo} />
            <Text className={classes.ehrTypeName}>{ehrType}</Text>
          </Box>
        </Box>
      </Box>

      <Grid gutter="xl">
        {detailLabels.map((label, index) => (
          <Grid.Col span={index === 4 ? 12 : 3} key={index}>
            <Text className={classes.label}>{label}</Text>
            <Text className={classes.value}>
              {detailValues && detailValues[index]?.length > 10 ? detailValues?.[index]?.slice(0, 10) : detailValues?.[index]}
            </Text>
          </Grid.Col>
        ))}
      </Grid>

      <Box className={classes.button}>
        <Button color="blue" onClick={handleButtonClick} disabled={showStep}>
          Fetch and Save Complete Patient Details
        </Button>
      </Box>
      {showStep && <StepComponent />}
    </Modal>
  );
};

export default PatientInfoModal;
